/* The menu keeps native links and dropdowns. This element only draws their rail. */
(() => {
  'use strict';
  if (customElements.get('header-menu-underline')) return;

  class HeaderMenuUnderline extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector(':scope > .nav-menu');
      this.tracks = this.querySelector('.header-menu-underline__tracks');
      this.root = this.closest('[data-titan-header]');
      if (!this.list || !this.tracks || !this.root) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.fine = matchMedia('(hover: hover) and (pointer: fine)');
      this.desktop = matchMedia('(min-width: 768px)');
      const followPointer = event => {
        if (!this.fine.matches || event.pointerType === 'touch') return;
        const link = this.topLink(event.target);
        if (!link || link === this.hovered) return;
        this.hovered = this.last = link;
        this.paint();
      };
      this.addEventListener('pointerover', followPointer, options);
      this.addEventListener('pointermove', followPointer, { ...options, passive: true });
      this.addEventListener('pointerleave', () => { this.hovered = null; this.paint(); }, options);
      this.addEventListener('focusin', event => {
        // Keyboard focus takes ownership even if the mouse rests on another
        // label. A subsequent pointer movement can take ownership back.
        this.hovered = null;
        this.focused = this.topLink(event.target);
        if (this.focused) this.last = this.focused;
        this.paint();
      }, options);
      this.addEventListener('focusout', event => { this.focused = this.topLink(event.relatedTarget); this.paint(); }, options);
      this.desktop.addEventListener('change', () => this.measure(), options);
      this.fine.addEventListener('change', () => { this.hovered = null; this.paint(); }, options);
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(this.list);
      this.resize.observe(this);
      this.changes = new MutationObserver(() => this.measure());
      this.changes.observe(this.list, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['aria-current', 'aria-expanded', 'hidden'] });
      this.settings = new MutationObserver(() => this.measure());
      this.settings.observe(this.root, { attributes: true, attributeFilter: ['data-header-underline', 'data-header-layout'] });
      document.fonts?.addEventListener('loadingdone', () => this.measure(), options);
      document.fonts?.ready.then(() => { if (this.isConnected) this.measure(); });
      this.measure();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resize?.disconnect();
      this.changes?.disconnect();
      this.settings?.disconnect();
      this.hovered = this.focused = this.last = this.previous = null;
    }

    topLink(target) {
      if (!(target instanceof Element) || !this.list.contains(target)) return null;
      let item = target.closest('li');
      while (item && item.parentElement !== this.list) item = item.parentElement.closest('li');
      return item?.querySelector(':scope > a[href]') || null;
    }

    measure() {
      if (!this.desktop.matches || this.root.dataset.headerUnderline !== 'long' || this.root.dataset.headerLayout === 'index' || !this.offsetWidth) {
        this.removeAttribute('data-underline-ready');
        this.removeAttribute('data-underline-active');
        this.previous = null;
        return;
      }
      const origin = this.getBoundingClientRect();
      const style = getComputedStyle(this);
      const gap = parseFloat(style.fontSize) * 0.4;
      const marker = parseFloat(style.getPropertyValue('--header-underline-marker')) || 2;
      const rail = parseFloat(style.getPropertyValue('--header-underline-rail')) || 1;
      this.items = Array.from(this.list.querySelectorAll(':scope > li > a[href]')).filter(link => link.getClientRects().length).map(link => {
        // Remove the optional hover scale before measuring. The baseline must
        // remain still while the lettering grows inside its unchanged box.
        const rect = link.getBoundingClientRect();
        const scale = parseFloat(getComputedStyle(link).scale) || 1;
        const width = rect.width / scale;
        const height = rect.height / scale;
        return { link, left: rect.left + (rect.width - width) / 2 - origin.left, top: rect.top + (rect.height - height) / 2 - origin.top, width, height };
      });
      const rows = [];
      for (const item of [...this.items].sort((a, b) => a.top - b.top || a.left - b.left)) {
        let row = rows.find(candidate => item.top < candidate.bottom - 1 && item.top + item.height > candidate.top + 1);
        if (!row) { row = { top: item.top, bottom: item.top + item.height, left: item.left, right: item.left + item.width, items: [] }; rows.push(row); }
        row.top = Math.min(row.top, item.top);
        row.bottom = Math.max(row.bottom, item.top + item.height);
        row.left = Math.min(row.left, item.left);
        row.right = Math.max(row.right, item.left + item.width);
        row.items.push(item);
        item.row = row;
      }
      this.rows = rows;
      this.tracks.replaceChildren(...rows.map((row, index) => {
        row.index = index;
        row.y = row.bottom + gap;
        const track = document.createElement('span');
        track.style.cssText = `left:${row.left}px;top:${row.y}px;width:${row.right - row.left}px`;
        for (const item of row.items) item.y = row.y + rail - marker;
        return track;
      }));
      this.toggleAttribute('data-underline-ready', !!this.items.length);
      this.paint();
    }

    paint() {
      if (!this.hasAttribute('data-underline-ready')) return;
      const current = this.topLink(this.list.querySelector('[aria-current="page"]'));
      const opened = this.topLink(this.list.querySelector('[aria-expanded="true"]'));
      // The last deliberate interaction stays selected after hover/focus leaves.
      // Current-page state is only the initial or removed-link fallback.
      const link = [this.last, opened, current].find(candidate => this.items.some(item => item.link === candidate));
      const item = this.items.find(candidate => candidate.link === link);
      this.toggleAttribute('data-underline-active', !!item);
      if (!item) { this.previous = null; return; }
      this.style.setProperty('--header-underline-x', `${item.left}px`);
      this.style.setProperty('--header-underline-y', `${item.y}px`);
      this.style.setProperty('--header-underline-width', `${item.width}px`);
      this.previous = item;
    }
  }
  customElements.define('header-menu-underline', HeaderMenuUnderline);
})();
